import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, HelpCircle, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getShortVersion } from "../utils/version";

export default function LegalPageShell({
  title,
  subtitle,
  version,
  effectiveDate,
  children,
  sections = [],
  namespace = "legal",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n, ready: i18nReady } = useTranslation([namespace, "common"]);

  const resolvedVersion = version || getShortVersion();

  const [activeSection, setActiveSection] = useState(null);
  const [showTOC, setShowTOC] = useState(false);
  const sectionRefs = useRef({});

  const safeT = (key, fallback, ns) => {
    if (!i18nReady) return fallback;
    try {
      const result = ns ? t(key, { ns }) : t(key);
      return typeof result === "string" && result !== key ? result : fallback;
    } catch {
      return fallback;
    }
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    const node = document.getElementById(id);
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "start" });
    sectionRefs.current[id]?.focus?.();
    if (window.innerWidth < 1024) setShowTOC(false);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );

    sections.forEach((section) => {
      const node = document.getElementById(section.id);
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [sections]);

  useEffect(() => {
    if (!location.hash) return;
    const hash = location.hash.replace("#", "");
    const node = document.getElementById(hash);
    if (!node) return;
    const raf = requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: "auto", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [location.hash]);

  const tocItems = useMemo(
    () =>
      sections.map((s) => ({
        id: s.id,
        label: s.label || s.id,
        children: s.children || [],
      })),
    [sections]
  );

  const hasTOC = tocItems.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center h-14 gap-3">
            <Link
              to="/"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:text-gray-300 dark:hover:bg-neutral-800"
              aria-label={safeT("back", "Back", "common")}
            >
              <ArrowLeft className="h-4.5 w-4.5" aria-hidden="true" />
            </Link>
            <h1 className="text-display-sm font-semibold text-gray-900 dark:text-white truncate">
              {title}
            </h1>
            {hasTOC && (
              <button
                type="button"
                onClick={() => setShowTOC(!showTOC)}
                className="ml-auto lg:hidden inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800"
                aria-expanded={showTOC}
                aria-controls="legal-toc"
              >
                <ChevronRight
                  className={`h-3.5 w-3.5 transition-transform ${showTOC ? "rotate-90" : ""}`}
                  aria-hidden="true"
                />
                {safeT("onThisPage", "On this page", "common")}
              </button>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 lg:py-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-4 lg:gap-6">
          {/* Table of Contents Sidebar */}
          <aside
            id="legal-toc"
            className="hidden lg:block lg:sticky lg:top-24 self-start"
            aria-label={safeT("onThisPage", "On this page", "common")}
          >
            <div className="rounded-2xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3 shadow-sm">
              <p className="px-1 pb-2 text-caption text-gray-500 dark:text-gray-400">
                {safeT("onThisPage", "On this page", "common")}
              </p>
              <nav className="space-y-1" role="navigation">
                {tocItems.map((item) => (
                  <div key={item.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(item.id)}
                      aria-current={activeSection === item.id ? "true" : undefined}
                      className={`w-full rounded-lg px-2.5 py-2 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 ${
                        activeSection === item.id
                          ? "bg-green-50 dark:bg-green-900/20 font-semibold text-green-800 dark:text-green-200"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800"
                      }`}
                    >
                      {item.label}
                    </button>
                    {item.children.length > 0 && (
                      <div className="ml-3 mt-0.5 space-y-0.5">
                        {item.children.map((child) => (
                          <button
                            key={child.id}
                            type="button"
                            onClick={() => scrollToSection(child.id)}
                            aria-current={activeSection === child.id ? "true" : undefined}
                            className={`w-full rounded-lg px-2 py-1.5 text-left text-xs transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 ${
                              activeSection === child.id
                                ? "font-medium text-green-700 dark:text-green-300 bg-green-50/50 dark:bg-green-900/10"
                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-800"
                            }`}
                          >
                            {child.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </aside>

          {/* Mobile TOC Drawer */}
          {hasTOC && showTOC && (
            <div
              className="lg:hidden fixed inset-0 z-40 bg-black/50"
              onClick={() => setShowTOC(false)}
              aria-hidden="true"
            />
          )}
          {hasTOC && showTOC && (
            <aside
              className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 shadow-xl"
              role="dialog"
aria-label={safeT("onThisPage", "On this page", "common")}
            >
              <div className="flex h-full flex-col">
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-neutral-800 p-4">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
{safeT("onThisPage", "On this page", "common")}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setShowTOC(false)}
                    className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-neutral-800"
                    aria-label={safeT("close", "Close", "common")}
                  >
                    <ChevronRight className="h-5 w-5 rotate-90" aria-hidden="true" />
                  </button>
                </div>
                <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                  {tocItems.map((item) => (
                    <div key={item.id}>
                      <button
                        type="button"
                        onClick={() => scrollToSection(item.id)}
                        aria-current={activeSection === item.id ? "true" : undefined}
                        className={`w-full rounded-lg px-3 py-2.5 text-left text-sm transition ${
                          activeSection === item.id
                            ? "bg-green-50 dark:bg-green-900/20 font-semibold text-green-800 dark:text-green-200"
                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800"
                        }`}
                      >
                        {item.label}
                      </button>
                      {item.children.length > 0 && (
                        <div className="ml-3 mt-0.5 space-y-0.5">
                          {item.children.map((child) => (
                            <button
                              key={child.id}
                              type="button"
                              onClick={() => scrollToSection(child.id)}
                              aria-current={activeSection === child.id ? "true" : undefined}
                              className={`w-full rounded-lg px-3 py-2 text-left text-xs transition ${
                                activeSection === child.id
                                  ? "font-medium text-green-700 dark:text-green-300 bg-green-50/50 dark:bg-green-900/10"
                                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-800"
                              }`}
                            >
                              {child.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
              </div>
            </aside>
          )}

          {/* Content Area */}
          <div className="min-w-0">
            {/* Page Header */}
            <header className="mb-6 lg:mb-8">
              <h2 className="text-display-xl font-semibold leading-tight tracking-tight text-gray-900 dark:text-white text-balance">
                {title}
              </h2>
              {subtitle && (
                <p className="mt-3 text-body-lg leading-relaxed text-gray-600 dark:text-gray-300 max-w-3xl">
                  {subtitle}
                </p>
              )}
              {(version || effectiveDate) && (
                <div className="mt-4 flex flex-wrap items-center gap-2 text-caption text-gray-500 dark:text-gray-500">
                  {version && (
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {safeT("version", "Version", "common")} {version}
                    </span>
                  )}
                  {version && effectiveDate && (
                    <span className="select-none" aria-hidden="true">
                      &middot;
                    </span>
                  )}
                  {effectiveDate && <span>{effectiveDate}</span>}
                </div>
              )}
            </header>

            {/* Content Card */}
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm">
              <div className="p-5 sm:p-6 lg:p-8 space-y-8 lg:space-y-10 legal-prose max-w-none">
                {children}
              </div>
            </div>

            {/* Need Help? CTA */}
            <div className="mt-6 bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-5 sm:p-6">
              <div className="flex items-start sm:items-center gap-3 flex-col sm:flex-row">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex-shrink-0">
                    <HelpCircle className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {safeT("needHelp", "Need Help?", "common")}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {safeT("supportDescription", "Visit our Support Center for guides, FAQ, and direct assistance.", "common")}
                    </p>
                  </div>
                </div>
                <Link
                  to="/support"
                  className="px-4 py-2 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition shrink-0"
                >
                  {safeT("supportCenter", "Support Center", "common")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}